<template>
  <div class="tag-select-input">
    <div class="tag-select-trigger" :class="{ 'is-open': isOpen }" tabindex="0" ref="trigger"
      @click="toggle" @keydown="handleKeydown">
      <div class="tag-select-current">
        <div v-if="selectedTag" class="tag-select-dot" :style="{ backgroundColor: selectedTag.color }">
          <vue-feather type="tag" size="11" />
        </div>
        <div v-else class="tag-select-dot tag-select-dot--ai">
          <img src="@/assets/icons/stars.png" alt="AI" class="tag-select-ai-icon" />
        </div>
        <span class="tag-select-label" :class="{ 'is-placeholder': !selectedTag }">
          {{ selectedTag ? selectedTag.name : placeholder }}
        </span>
      </div>
      <vue-feather :type="isOpen ? 'chevron-up' : 'chevron-down'" size="18" class="tag-select-chevron" />
    </div>
    <FloatingDropdown :open="isOpen" :triggerRef="$refs.trigger" @dismiss="close">
      <div class="tag-select-menu">
        <div class="tag-select-search">
          <input ref="search" v-model="query" placeholder="Search tags..."
            autocomplete="nope" data-lpignore="true" data-form-type="other" @click.stop />
        </div>
        <div class="tag-select-list">
          <div class="tag-select-item" :data-active="!modelValue" @click.stop="select('')">
            <div class="tag-select-icon tag-select-icon--ai">
              <img src="@/assets/icons/stars.png" alt="AI" class="tag-select-ai-icon" />
            </div>
            <span>{{ placeholder }}</span>
          </div>
          <div class="tag-select-item" v-for="t of filteredTags" :key="t.id"
            :data-active="modelValue === t.id" @click.stop="select(t.id)">
            <div class="tag-select-icon" :style="{ backgroundColor: t.color }">
              <vue-feather type="tag" size="12" />
            </div>
            <span>{{ t.name }}</span>
          </div>
          <div v-if="filteredTags.length === 0" class="tag-select-empty">No tags found</div>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'

// Input-styled tag picker: a full-width field that opens a searchable,
// scrollable dropdown of colored tags (plus a leading "no tag" option). Mirrors
// DropdownInput's trigger look and the sidebar settings menu's option rows.
export default {
  name: 'TagSelectInput',
  components: { 'vue-feather': VueFeather, FloatingDropdown },
  props: {
    // Selected tag id, or '' for the leading (no-tag) option.
    modelValue: { type: String, default: '' },
    // Array of { id, name, color } tags.
    tags: { type: Array, required: true },
    // Label for the leading no-tag option.
    placeholder: { type: String, default: 'No tag' }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return { isOpen: false, query: '' }
  },
  computed: {
    selectedTag() {
      if (!this.modelValue) return null
      return this.tags.find(t => t.id === this.modelValue) || null
    },
    filteredTags() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.tags
      return this.tags.filter(t => t.name.toLowerCase().includes(q))
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.query = ''
        this.$nextTick(() => this.$refs.search?.focus())
      }
    }
  },
  mounted() {
    window.addEventListener('mousedown', this.handleClickOutside)
  },
  unmounted() {
    window.removeEventListener('mousedown', this.handleClickOutside)
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen
    },
    close() {
      this.isOpen = false
    },
    select(tagId) {
      const value = tagId || ''
      this.$emit('update:modelValue', value)
      this.$emit('change', value)
      this.isOpen = false
    },
    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.toggle()
      } else if (event.key === 'Escape') {
        this.close()
      }
    },
    handleClickOutside(event) {
      if (!this.isOpen) return
      const trigger = this.$refs.trigger
      if (trigger && trigger.contains(event.target)) return
      // The menu is teleported to <body>, so match on its class instead of refs.
      if (event.target.closest && event.target.closest('.tag-select-menu')) return
      this.isOpen = false
    }
  }
}
</script>

<style scoped>
.tag-select-input {
  position: relative;
  width: 100%;
}

.tag-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  padding: 0 12px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.tag-select-trigger:hover {
  border-color: #9ca3af;
}

.tag-select-trigger.is-open,
.tag-select-trigger:focus {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.tag-select-current {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.tag-select-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFF;
  flex-shrink: 0;
}

.tag-select-dot--ai {
  background: #f3f0ff;
}

.tag-select-ai-icon {
  width: 13px;
  height: 13px;
}

.tag-select-label {
  font-size: 0.9375rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-select-label.is-placeholder {
  color: #6b7280;
}

.tag-select-chevron {
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 8px;
  transition: color 0.15s ease;
}

.tag-select-trigger.is-open .tag-select-chevron {
  color: var(--color-primary, #6a16f0);
}
</style>

<style>
/* Menu is teleported to <body> by FloatingDropdown — styles must be unscoped. */
.tag-select-menu {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  padding: 4px;
  max-height: min(var(--fd-max-height, 9999px), 280px);
  overflow-y: auto;
}

.tag-select-menu .tag-select-search {
  position: sticky;
  top: 0;
  background: #FFF;
  padding: 4px 4px 6px;
  z-index: 1;
}

.tag-select-menu .tag-select-search input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  outline: none;
  box-sizing: border-box;
}

.tag-select-menu .tag-select-search input:focus {
  border-color: var(--color-primary, #6a16f0);
}

.tag-select-menu .tag-select-list {
  display: flex;
  flex-direction: column;
}

.tag-select-menu .tag-select-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: background 0.1s ease;
}

.tag-select-menu .tag-select-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-select-menu .tag-select-item:hover {
  background: #f3f4f6;
}

.tag-select-menu .tag-select-item[data-active="true"] {
  background: #e9edff;
}

.tag-select-menu .tag-select-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFF;
  flex-shrink: 0;
}

.tag-select-menu .tag-select-icon--ai {
  background: #f3f0ff;
}

.tag-select-menu .tag-select-ai-icon {
  width: 13px;
  height: 13px;
}

.tag-select-menu .tag-select-empty {
  padding: 12px 10px;
  font-size: 0.8125rem;
  color: #9ca3af;
  text-align: center;
}
</style>
